import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, AccessoryChangeEvent } from '@theme/events';

/**
 * Buybox Flow — 3-step guided purchase accordion.
 *
 * Step 1: Choose Your System (color + filter count)
 * Step 2: Complete Your Setup (setup accessories carousel + modal, system_complete_setup_product_list)
 * Step 3: Add Accessories (optional accessories carousel + modal, custom.accessories)
 */
class BuyboxFlow extends HTMLElement {
  /** @type {Object|null} */
  #data = null;

  /** @type {Array} */
  #variants = [];

  /** @type {string[]} */
  #selectedOptions = [];

  /** @type {number} */
  #colorOptionIndex = 0;

  /** @type {number} */
  #filterOptionIndex = 1;

  /** @type {boolean} */
  #filterSelected = false;

  /** @type {Map<string, {variantId: string, title: string, price: number}>} */
  #setupSelected = new Map();

  /** @type {Map<string, {variantId: string, title: string, price: number}>} */
  #accessoriesSelected = new Map();

  /** @type {Object<string, string>} Map of variant id → preview image URL */
  #componentImages = {};

  /** @type {Object<string, string>} Map of variant id → alternate overlay (used when the item's quantity is doubled) */
  #componentImagesAlt = {};

  /** @type {Object<string, string>} Map of filter option value → preview image URL (color-independent fallback) */
  #componentImagesByFilter = {};

  /** @type {Object<string, string>} Map of color option value → dedicated Step 1 preview image (custom.buybox_color_image) */
  #colorImages = {};

  /** @type {string|null} Base image shown under Step 2 component layers */
  #step2BaseImage = null;

  /**
   * Setup items whose title contains one of these words (case-insensitive) use
   * a full/opaque base image instead of a transparent overlay layer — e.g.
   * Fluoride ships as a full composite, not a stackable PNG.
   * @type {string[]}
   */
  #fullImageTitleKeywords = ['fluoride'];

  /** @type {{value: string, label: string}|null} Selected Book-a-Call time slot */
  #bookSlot = null;

  /** @type {HTMLElement|null} The gallery preview overlay element (moves between mounts) */
  #overlayEl = null;

  /** Below this width, the layered preview mounts inline after Step 1 instead of in the PDP gallery. */
  #mobileMq = window.matchMedia('(max-width: 749px)');

  /** When set, skip the gallery cross-fade so reduced-motion users get instant swaps. */
  #reduceMotionMq = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** @type {'filter'|'color'|null} What the mobile Step 1 mount should show: the filter's layered image or the selected color's featured image. */
  #step1PreviewMode = null;

  /** @type {1|2} Which step the shopper is currently interacting with — drives whether the preview shows the Step 1 image or the Step 2 composite. */
  #activePreviewStep = 1;

  /** @type {{scroller: HTMLElement, controls: HTMLElement, track: HTMLElement, thumb: HTMLElement, prev: HTMLButtonElement|null, next: HTMLButtonElement|null}[]} Carousel + its controls row (arrows + scroll-progress bar) */
  #carousels = [];

  connectedCallback() {
    this.#parseData();
    this.#initAnalytics();
    this.#bindEvents();
    this.#initBookCall();
    this.#initCarousels();
    this.#updateSetupCardImages();
    this.#updateSetupCardPrices();
    // Normalize the server-rendered What's Included main line (name separator,
    // price, and box-contents filter count) to the resolved initial variant.
    this.#updateWhatsIncluded();
    // Add to Cart stays disabled until the shopper picks a filter count.
    // Deferred so the separately-rendered add-to-cart-component is upgraded.
    requestAnimationFrame(() => this.#lockAddToCartUntilFilter());
    // On mobile, show the selected variant's image in the Step 1 inline mount by
    // default (no interaction required).
    if (this.#mobileMq.matches) this.#refreshGalleryPreview();
    // Honour a filter radio the browser restored on a back/forward navigation
    // (#555). Runs after the setup UI is initialised (so its re-render has its
    // dependencies) and synchronously before the rAF Add-to-Cart lock above, so
    // the lock sees the restored selection and leaves Add to Cart enabled.
    this.#restoreFilterSelection();
    // Re-mount and re-render the preview when crossing the mobile breakpoint.
    this.#mobileMq.addEventListener('change', this.#handleBreakpointChange);
    window.addEventListener('resize', this.#rebuildAllProgress);
    // #555: the browser restores the step-1 filter radio on a back/forward
    // navigation, but that restoration can land AFTER this upgrade runs, so the
    // connectedCallback check above may see nothing. `pageshow` fires once the
    // page (and its restored form fields) is fully presented — on bfcache
    // restores (persisted) and on reload-type back/forward alike — so re-run
    // the restore there too. Idempotent: it no-ops once a filter is selected.
    window.addEventListener('pageshow', this.#restoreFilterSelection);
    this.#logSetupData();
  }

  disconnectedCallback() {
    this.#mobileMq.removeEventListener('change', this.#handleBreakpointChange);
    window.removeEventListener('resize', this.#rebuildAllProgress);
    window.removeEventListener('pageshow', this.#restoreFilterSelection);
    this.#analyticsAbortController?.abort();
  }

  #handleBreakpointChange = () => {
    this.#refreshGalleryPreview();
  };

  // ─── Analytics (dataLayer) ────────────────────────────────────────────────

  /** @type {Set<string>} Dedup keys for once-per-page-load events */
  #trackedOnce = new Set();

  /**
   * Scopes the analytics cart listener to this element's lifetime. The listener
   * binds to the surrounding `.shopify-section`, which outlives this element
   * across section morphs — without this, a re-rendered buybox-flow would leave
   * the old listener attached and double-count add-to-cart. Mirrors the
   * AbortController pattern in product-form.js.
   * @type {AbortController|null}
   */
  #analyticsAbortController = null;

  /**
   * Pushes a configurator event to the GTM dataLayer (GA4 wiring lives in GTM;
   * see boroux-experimentation docs/guides/tracking/CONFIGURATOR-ANALYTICS.md).
   * Analytics must never break the flow, so failures are swallowed.
   * @param {string} event
   * @param {Object} [params]
   */
  #track(event, params = {}) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event,
        configurator: 'buybox_flow',
        product_id: String(this.#data?.productId ?? ''),
        product_handle: this.#productHandle(),
        ...params,
      });
    } catch (e) {
      /* never break the flow over analytics */
    }
  }

  /**
   * Product handle for event params. Derived from the stable top-level
   * `productUrl` rather than `bookCall.productHandle`, so it keeps working if
   * the Book a Call block is disabled or its settings are restructured.
   * @returns {string}
   */
  #productHandle() {
    const fromUrl = String(this.#data?.productUrl ?? '')
      .split('?')[0]
      .split('/')
      .filter(Boolean)
      .pop();
    return fromUrl || this.#data?.bookCall?.productHandle || '';
  }

  /** Fires an event only once per page load, keyed by event + key. */
  #trackOnce(event, params = {}, key = '') {
    const k = `${event}|${key}`;
    if (this.#trackedOnce.has(k)) return;
    this.#trackedOnce.add(k);
    this.#track(event, params);
  }

  /** First configuration interaction of the page load (any trigger). */
  #trackStart(trigger) {
    this.#trackOnce('configurator_start', { trigger });
  }

  #initAnalytics() {
    this.#trackOnce('configurator_view', { step: 1 });
    this.#trackOnce('configurator_step_view', { step: 1, trigger: 'initial' }, 'step1');
    // Configured-bundle ATC: the product form POSTs main + nested setup items +
    // accessories in one call, then dispatches cart:update on the section.
    // Elevar reports each line separately; this event is the single
    // bundle-level signal (value, step_reached, composition).
    this.#analyticsAbortController = new AbortController();
    this.closest('.shopify-section')?.addEventListener(
      ThemeEvents.cartUpdate,
      event => {
        const d = /** @type {any} */ (event).detail?.data;
        if (!d || d.source !== 'product-form-component' || d.didError) return;
        if (String(d.productId ?? '') !== String(this.#data?.productId ?? '')) return;
        this.#trackAddToCart();
      },
      { signal: this.#analyticsAbortController.signal }
    );
  }

  /** Numeric filter count from the selected option value (e.g. "4 Filters" → 4). */
  #filterCount() {
    return parseInt(String(this.#selectedOptions[this.#filterOptionIndex] ?? '').match(/\d+/)?.[0] ?? '0', 10);
  }

  /** Highest step the shopper has reached (active or completed panel). */
  #stepReached() {
    let max = 1;
    for (const panel of this.querySelectorAll('.buybox-flow__panel[data-step]')) {
      const el = /** @type {HTMLElement} */ (panel);
      if (el.dataset.state === 'active' || el.dataset.state === 'completed') {
        max = Math.max(max, parseInt(el.dataset.step ?? '1', 10));
      }
    }
    return max;
  }

  #trackAddToCart() {
    // The configured variant, not the sync fallback: #findVariantForSync()
    // substitutes the initial filter option when none is chosen, which would
    // report a variant the shopper never selected.
    const variant = this.#findVariant() ?? this.#findVariantForSync();
    const setup = [...this.#setupSelected.values()];
    const accessories = [...this.#accessoriesSelected.values()];
    const addonsTotal = [...setup, ...accessories].reduce(
      (sum, item) => sum + item.price * this.#itemQuantity(item),
      0
    );
    // The buybox has no quantity selector today, but read the form's quantity
    // if one is ever added so value and item_count stay correct.
    const qtyInput = /** @type {HTMLInputElement|null} */ (
      this.closest('.shopify-section')?.querySelector('product-form-component input[name="quantity"]')
    );
    const systemQty = Math.max(1, parseInt(qtyInput?.value ?? '1', 10) || 1);
    this.#track('configurator_add_to_cart', {
      step_reached: this.#stepReached(),
      value: ((variant?.price ?? 0) * systemQty + addonsTotal) / 100,
      currency: 'USD',
      system_variant_id: String(variant?.id ?? ''),
      system_quantity: systemQty,
      color: this.#selectedOptions[this.#colorOptionIndex] ?? '',
      filter_count: this.#filterCount(),
      addon_count: setup.length,
      accessory_count: accessories.length,
      item_count: systemQty + setup.length + accessories.length,
      addon_ids: setup.map(i => i.variantId).join(','),
      accessory_ids: accessories.map(i => i.variantId).join(','),
      subscription: [...setup, ...accessories].some(i => i.sellingPlanId) ? 'yes' : 'no',
    });
  }

  // ─── Init ─────────────────────────────────────────────────────────────────

  /**
   * Debug helper — dumps every "Complete Your Setup" (Step 2) and accessory
   * (Step 3) card's data alongside whether its variant has a mapped preview
   * image, so mismatches between the metaobject and the cards are easy to spot.
   */
  #logSetupData() {
    for (const step of [2, 3]) {
      const panel = this.querySelector(`[data-step="${step}"]`);
      if (!panel) continue;
      const cards = [...panel.querySelectorAll('.buybox-flow__setup-confirm')].map(btn => {
        const b = /** @type {HTMLElement} */ (btn);
        const variantId = b.dataset.variantId ?? '';
        return {
          variantId,
          title: b.dataset.title ?? '',
          basePrice: b.dataset.basePrice ?? '',
          hasMappedImage: Boolean(this.#componentImages[variantId]),
          mappedImage: this.#componentImages[variantId] ?? null,
        };
      });
      console.log(`[buybox] step ${step} (${step === 2 ? 'Complete Your Setup' : 'Add Accessories'}) cards`, {
        count: cards.length,
        cards,
      });
    }
  }

  #parseData() {
    const script = this.querySelector('script[ref="productData"]');
    if (!script) return;
    try {
      this.#data = JSON.parse(script.textContent);
      this.#variants = this.#data.variants;
      this.#selectedOptions = [...this.#data.initialOptions];
      this.#colorOptionIndex = this.#data.colorOptionIndex ?? 0;
      this.#filterOptionIndex = this.#data.filterOptionIndex ?? 1;
      this.#componentImages = this.#data.componentImages ?? {};
      this.#componentImagesAlt = this.#data.componentImagesAlt ?? {};
      this.#componentImagesByFilter = this.#data.componentImagesByFilter ?? {};
      this.#colorImages = this.#data.colorImages ?? {};
      this.#step2BaseImage = this.#data.step2BaseImage ?? null;
      this.#selectedOptions[this.#filterOptionIndex] = '';
      console.log('[buybox] parsed data', {
        filterOptionIndex: this.#filterOptionIndex,
        componentImages: this.#componentImages,
        componentImagesByFilter: this.#componentImagesByFilter,
        variantCount: this.#variants.length,
      });
      const setupDebug = this.#data.setupDebug;
      if (setupDebug) {
        const included = setupDebug.entries.filter(e => e.reason === 'included');
        console.log('[buybox] Complete Your Setup metaobject', {
          rawCount: setupDebug.rawCount,
          includedCount: included.length,
          skipped: setupDebug.entries.filter(e => e.reason !== 'included'),
          entries: setupDebug.entries,
        });
      }
    } catch (e) {
      console.error('BuyboxFlow: failed to parse product data', e);
    }
  }

  #bindEvents() {
    // Step 1 — direct bindings (always in DOM, always active)
    for (const input of this.querySelectorAll('[ref="colorInput"]')) {
      input.addEventListener('change', this.#handleColorChange);
    }
    for (const input of this.querySelectorAll('[ref="filterInput"]')) {
      input.addEventListener('change', this.#handleFilterChange);
    }
    this.querySelector('[ref="continueCta"]')?.addEventListener('click', this.#handleContinue);
    this.querySelector('[ref="continueCta2"]')?.addEventListener('click', () => this.#completeStep(2));
    this.querySelector('[ref="doneBtn"]')?.addEventListener('click', this.#handleDone);

    // Steps 2 & 3 — delegated so they work regardless of panel visibility
    this.addEventListener('click', this.#handleDelegatedClick);
    this.addEventListener('change', this.#handleDelegatedChange);
  }

  // ─── Step 1 ───────────────────────────────────────────────────────────────

  #handleColorChange = async event => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    this.#selectedOptions[parseInt(input.dataset.optionIndex ?? this.#colorOptionIndex)] = input.value;
    this.#trackStart('color');
    this.#track('configurator_select', {
      step: 1,
      option: 'color',
      option_value: input.value,
      variant_id: String(this.#findVariantForSync()?.id ?? ''),
    });
    this.#updateColorLabel(input.value);
    this.#updateFilterPrices();
    this.#updateWhatsIncluded();
    // A new color may resolve to an out-of-stock variant (or back in stock),
    // so re-evaluate the Continue button once a filter has been chosen.
    if (this.#filterSelected) this.#enableContinue();
    // Mobile Step 1 now shows the selected color's featured image.
    this.#step1PreviewMode = 'color';
    this.#activePreviewStep = 1;
    // Changing color invalidates Step 2/3 selections (their variant ids change)
    // — clear them and hide the composite overlay so the native, color-correct
    // gallery shows again. The fresh cards re-render in #syncVariant.
    this.#resetSetupSelections();
    // On mobile, show the color's featured image in the inline mount so the
    // change is visible without scrolling up. On desktop leave the overlay
    // hidden and let the native gallery update. #syncVariant re-renders the
    // cards and re-applies this (see #refreshSetupStepsFromDoc) so it survives.
    if (this.#mobileMq.matches) this.#refreshGalleryPreview();
    await this.#syncVariant();
  };

  /**
   * Applies a step-1 filter-count selection to component state and every
   * dependent piece of UI. Shared by the user change handler and the
   * back/forward restore so the two paths can't drift. Returns the
   * #syncVariant promise so callers can await it. Deliberately holds no
   * analytics — a restore is not a fresh user selection.
   * @param {HTMLInputElement} input - the chosen filter radio
   */
  #applyFilterSelection(input) {
    this.#selectedOptions[parseInt(input.dataset.optionIndex ?? this.#filterOptionIndex)] = input.value;
    this.#filterSelected = true;
    // Choosing a filter size shows that size's own layered image (mobile).
    this.#step1PreviewMode = 'filter';
    this.#activePreviewStep = 1;
    this.#enableContinue();
    this.#updateWhatsIncluded();
    // Filter-scaled items (e.g. Fluoride) change quantity with the count —
    // re-render their What's Included rows so the line prices follow.
    this.#updateSetupWI();
    this.#refreshGalleryPreview();
    // Swap filter-scaled card thumbnails (e.g. Fluoride) for the new count.
    this.#updateSetupCardImages();
    // Rescale filter-scaled card prices (e.g. Fluoride) for the new count.
    this.#updateSetupCardPrices();
    // Re-dispatch so any filter-scaled item (e.g. Fluoride) updates its quantity
    // and price total for the new filter count.
    this.#dispatchAccessoryChange();
    return this.#syncVariant();
  }

  #handleFilterChange = async event => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    this.#trackStart('filter_count');
    const sync = this.#applyFilterSelection(input);
    const selectVariant = this.#findVariant();
    this.#track('configurator_select', {
      step: 1,
      option: 'filter_count',
      option_value: input.value,
      filter_count: this.#filterCount(),
      variant_id: String(selectVariant?.id ?? ''),
      price: selectVariant ? selectVariant.price / 100 : 0,
    });
    await sync;
  };

  /**
   * Back/forward restore (#555): on a `back_forward` navigation the browser
   * re-checks the previously selected step-1 filter radio from form history
   * without firing a `change` event, so the component never learns a filter is
   * selected and Continue (plus Add to Cart) stay disabled — the shopper is
   * stuck. Honour an already-checked radio on load by applying its selection.
   * No-op on a normal load, where no filter radio is checked.
   */
  #restoreFilterSelection = () => {
    if (this.#filterSelected) return;
    const checked = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[ref="filterInput"]:checked')
    );
    if (checked) this.#applyFilterSelection(checked);
  };

  #handleContinue = () => {
    const btn = /** @type {HTMLButtonElement} */ (this.querySelector('[ref="continueCta"]'));
    if (!btn || btn.disabled) return;
    // Out-of-stock variant: open the Klaviyo back-in-stock form instead of
    // advancing to Step 2.
    if (btn.dataset.notifyMode === 'true') {
      // Demand signal for an out-of-stock configuration. Klaviyo owns the form
      // and its submit, so the notify intent is the moment we can measure.
      const variant = this.#findVariant();
      this.#track('configurator_notify_click', {
        step: 1,
        color: this.#selectedOptions[this.#colorOptionIndex] ?? '',
        filter_count: this.#filterCount(),
        variant_id: String(variant?.id ?? ''),
      });
      this.#openNotifyForm();
      return;
    }
    this.#completeStep(1);
  };

  /**
   * Opens the Klaviyo back-in-stock form for the current variant by clicking the
   * button Klaviyo injects into #klaviyo-bis-button-container. The sibling
   * klaviyo-back-in-stock block already reveals the matching form off the
   * VariantUpdate event #syncVariant dispatches, so the visible form (and thus
   * the injected button) is the current OOS variant's. Klaviyo injects that
   * button asynchronously, so poll for it briefly. Mirrors notify-url-param.js.
   */
  #openNotifyForm() {
    const selector = '#klaviyo-bis-button-container button';
    // Just trigger the click — Klaviyo opens its own overlay modal, so there's
    // no need to scroll to the (hidden, bottom) button, which would otherwise
    // jump the page down.
    const click = btn => btn.click();
    const existing = /** @type {HTMLElement|null} */ (document.querySelector(selector));
    if (existing) {
      click(existing);
      return;
    }
    const start = Date.now();
    const interval = setInterval(() => {
      const btn = /** @type {HTMLElement|null} */ (document.querySelector(selector));
      if (btn) {
        clearInterval(interval);
        click(btn);
        return;
      }
      if (Date.now() - start > 8000) clearInterval(interval);
    }, 100);
  }

  // ─── Steps 2 & 3 — delegated ─────────────────────────────────────────────

  #handleDelegatedClick = event => {
    const target = /** @type {HTMLElement} */ (event.target);

    // Close button and backdrop are handled by dialog-component (on:click='/closeDialog')

    // Confirm button
    const confirmBtn = /** @type {HTMLElement|null} */ (target.closest('.buybox-flow__setup-confirm'));
    if (confirmBtn) {
      this.#handleConfirm(confirmBtn);
      return;
    }

    // Card click — ignore if inside an open dialog
    if (target.closest('.buybox-flow__setup-dialog')) return;

    const card = /** @type {HTMLElement|null} */ (target.closest('.buybox-flow__setup-card'));
    if (card) {
      // Footer split: the "Add" button selects/deselects; "Details" (and tapping
      // the image) opens the details modal.
      if (target.closest('.buybox-flow__setup-add')) {
        this.#handleAddToggle(card);
      } else if (
        target.closest('.buybox-flow__setup-details') ||
        target.closest('.buybox-flow__setup-card-img-wrap')
      ) {
        this.#openCardModal(card);
      }
    }
  };

  #handleDelegatedChange = event => {
    const target = /** @type {HTMLElement} */ (event.target);
    const dialog = /** @type {HTMLElement|null} */ (target.closest('dialog'));
    if (!dialog) return;

    // Frequency select changed — update confirm button price
    if (target.classList.contains('buybox-flow__frequency-select')) {
      const select = /** @type {HTMLSelectElement} */ (target);
      const price = parseInt(select.options[select.selectedIndex]?.dataset.price ?? '0');
      this.#syncConfirmBtnPrice(dialog, price);
      return;
    }

    // Purchase type radio changed
    if (!target.classList.contains('buybox-flow__setup-radio')) return;
    const radio = /** @type {HTMLInputElement} */ (target);
    const freqWrap = /** @type {HTMLElement|null} */ (dialog.querySelector('.buybox-flow__frequency-wrap'));

    if (radio.value === 'subscribe') {
      // Show frequency dropdown and use the currently selected plan's price
      if (freqWrap) freqWrap.hidden = false;
      const select = /** @type {HTMLSelectElement|null} */ (dialog.querySelector('.buybox-flow__frequency-select'));
      const price = parseInt(select?.options[select.selectedIndex]?.dataset.price ?? '0');
      this.#syncConfirmBtnPrice(dialog, price);
    } else {
      // Hide frequency dropdown and revert to one-time price
      if (freqWrap) freqWrap.hidden = true;
      this.#syncConfirmBtnPrice(dialog, parseInt(radio.dataset.price ?? '0'));
    }
  };

  /**
   * Toggles selection for a setup/accessory card via its "Add" button.
   * @param {HTMLElement} card
   */
  #handleAddToggle(card) {
    const variantId = card.dataset.variantId ?? '';
    const step = parseInt(card.closest('[data-step]')?.dataset.step ?? '2');
    const map = step === 3 ? this.#accessoriesSelected : this.#setupSelected;
    const hasSubscription = card.dataset.hasSubscription === 'true';

    // Already added → remove.
    if (map.has(variantId)) {
      this.#removeItem(variantId, step);
      return;
    }
    // Products with a subscription still need the modal to choose a plan;
    // one-time-only products are selected directly, skipping the modal.
    if (hasSubscription) {
      this.#openCardModal(card);
    } else {
      this.#selectCardDirect(card, step);
    }
  }

  /**
   * Opens a setup card's purchase modal, resetting it to the one-time option.
   * @param {HTMLElement} card
   */
  #openCardModal(card) {
    const dialogComponent = /** @type {any|null} */ (
      card.closest('.buybox-flow__carousel-item')?.querySelector('dialog-component')
    );
    const dialog = /** @type {HTMLDialogElement|null} */ (
      dialogComponent?.querySelector('.buybox-flow__setup-dialog')
    );
    if (!dialogComponent || !dialog) return;

    this.#track('configurator_modal_open', {
      step: parseInt(card.closest('[data-step]')?.dataset.step ?? '2', 10),
      modal_type: 'product_details',
      item_title: card.querySelector('.buybox-flow__setup-card-name')?.textContent?.trim() ?? '',
      variant_id: card.dataset.variantId ?? '',
    });

    // Reset: one-time radio checked, frequency dropdown hidden, button price = base price
    const oneTimeRadio = /** @type {HTMLInputElement|null} */ (
      dialog.querySelector('.buybox-flow__setup-radio[value="one-time"]')
    );
    const freqWrap = /** @type {HTMLElement|null} */ (dialog.querySelector('.buybox-flow__frequency-wrap'));
    if (oneTimeRadio) {
      oneTimeRadio.checked = true;
      if (freqWrap) freqWrap.hidden = true;
      this.#syncConfirmBtnPrice(dialog, parseInt(oneTimeRadio.dataset.price ?? '0'));
    } else {
      const confirmBtn = /** @type {HTMLElement|null} */ (dialog.querySelector('.buybox-flow__setup-confirm'));
      this.#syncConfirmBtnPrice(dialog, parseInt(confirmBtn?.dataset.basePrice ?? '0'));
    }
    dialogComponent.showDialog();
  }

  /**
   * Selects a one-time-only card without opening the modal, reading its details
   * from the (hidden) confirm button so pricing/flags stay identical.
   * @param {HTMLElement} card
   * @param {number} step
   */
  #selectCardDirect(card, step) {
    const confirmBtn = /** @type {HTMLElement|null} */ (
      card.closest('.buybox-flow__carousel-item')?.querySelector('.buybox-flow__setup-confirm')
    );
    if (!confirmBtn) return;
    const item = {
      variantId: confirmBtn.dataset.variantId ?? card.dataset.variantId ?? '',
      title: confirmBtn.dataset.title ?? '',
      price: parseInt(confirmBtn.dataset.basePrice ?? '0'),
      sellingPlanId: null,
      color: confirmBtn.dataset.color ?? '',
      single: confirmBtn.dataset.singleSelect === 'true',
      qtyMatchesFilters: confirmBtn.dataset.qtyMatchesFilters === 'true',
    };
    this.#commitSelection(item, step);
  }

  /**
   * Removes a selected setup/accessory item and syncs the UI + cart eligibility.
   * @param {string} variantId
   * @param {number} step
   */
  #removeItem(variantId, step) {
    const map = step === 3 ? this.#accessoriesSelected : this.#setupSelected;
    const removed = map.get(variantId);
    if (removed) {
      this.#track('configurator_addon_toggle', {
        step,
        action: 'remove',
        item_title: removed.title,
        variant_id: variantId,
        price: removed.price / 100,
        quantity: this.#itemQuantity(removed),
      });
    }
    map.delete(variantId);
    this.#updateCardState(variantId, false);
    step === 3 ? this.#updateAccessoriesWI() : this.#updateSetupWI();
    this.#dispatchAccessoryChange();
    this.#refreshGalleryPreview();
  }

  #handleConfirm(confirmBtn) {
    const dialog = /** @type {HTMLDialogElement|null} */ (confirmBtn.closest('dialog'));
    const variantId = confirmBtn.dataset.variantId ?? '';
    const title = confirmBtn.dataset.title ?? '';
    const color = confirmBtn.dataset.color ?? '';
    const single = confirmBtn.dataset.singleSelect === 'true';
    const basePrice = parseInt(confirmBtn.dataset.basePrice ?? '0');
    const selectedRadio = /** @type {HTMLInputElement|null} */ (
      dialog?.querySelector('.buybox-flow__setup-radio:checked')
    );

    let price = basePrice;
    let sellingPlanId = null;

    if (selectedRadio?.value === 'subscribe') {
      // Read price + plan ID from the frequency select
      const select = /** @type {HTMLSelectElement|null} */ (dialog?.querySelector('.buybox-flow__frequency-select'));
      const opt = select?.options[select.selectedIndex];
      price = parseInt(opt?.dataset.price ?? String(basePrice));
      sellingPlanId = opt?.value ?? null;
    } else if (selectedRadio) {
      price = parseInt(selectedRadio.dataset.price ?? String(basePrice));
    }

    const step = parseInt(confirmBtn.dataset.step ?? '2');
    const qtyMatchesFilters = confirmBtn.dataset.qtyMatchesFilters === 'true';
    const item = { variantId, title, price, sellingPlanId, color, single, qtyMatchesFilters };

    this.#commitSelection(item, step);

    // Use dialog-component's closeDialog for proper scroll restore
    const dialogComponent = /** @type {any|null} */ (confirmBtn.closest('dialog-component'));
    if (dialogComponent) {
      dialogComponent.closeDialog();
    } else {
      dialog?.close();
    }
  }

  /**
   * Adds an item to the Step 2 (setup) or Step 3 (accessories) selection and
   * syncs What's Included, card state, preview, and cart eligibility. Shared by
   * the modal confirm button and direct (no-modal) card selection.
   * @param {{variantId: string, title: string, price: number, sellingPlanId: string|null, color: string, single: boolean, qtyMatchesFilters: boolean}} item
   * @param {number} step
   */
  #commitSelection(item, step) {
    if (step === 3) {
      this.#accessoriesSelected.set(item.variantId, item);
      this.#updateAccessoriesWI();
    } else {
      // Mutually-exclusive group: adding a single-select item (e.g. a stand)
      // silently removes any other single-select item already chosen.
      if (item.single) {
        for (const [vid, existing] of this.#setupSelected) {
          if (existing.single && vid !== item.variantId) {
            this.#track('configurator_addon_toggle', {
              step,
              action: 'remove',
              reason: 'swap',
              item_title: existing.title,
              variant_id: vid,
              price: existing.price / 100,
              quantity: this.#itemQuantity(existing),
            });
            this.#setupSelected.delete(vid);
            this.#updateCardState(vid, false);
          }
        }
      }
      this.#setupSelected.set(item.variantId, item);
      this.#updateSetupWI();
    }
    this.#track('configurator_addon_toggle', {
      step,
      action: 'add',
      item_title: item.title,
      variant_id: item.variantId,
      price: item.price / 100,
      quantity: this.#itemQuantity(item),
      subscription: item.sellingPlanId ? 'yes' : 'no',
    });

    this.#updateCardState(item.variantId, true);
    // Adding a setup item switches the preview to the Step 2 composite.
    this.#activePreviewStep = 2;
    this.#dispatchAccessoryChange();
    this.#refreshGalleryPreview();
  }

  #syncConfirmBtnPrice(dialog, price) {
    if (!this.#data) return;
    const btn = /** @type {HTMLElement|null} */ (dialog.querySelector('.buybox-flow__setup-confirm'));
    if (!btn) return;
    const prefix = btn.dataset.confirmPrefix ?? 'Add to setup';
    // Filter-scaled items (e.g. Fluoride) add multiple units, so the button
    // label must show the scaled total, matching the card price and What's
    // Included line.
    const qty = btn.dataset.qtyMatchesFilters === 'true' ? this.#filterQuantityMultiplier() : 1;
    btn.textContent = `${prefix} · ${this.#formatMoney(price * qty)}`;
  }

  #handleDone = () => {
    this.#completeStep(3);
    // Review Order is the trigger that expands Items In Your Order (it was
    // activated earlier, alongside the ATC button, but kept collapsed).
    const whatsIncluded = this.querySelector('[ref="whatsIncluded"]');
    if (whatsIncluded) whatsIncluded.setAttribute('data-state', 'active');
    const wiDetails = /** @type {HTMLDetailsElement|null} */ (this.querySelector('.buybox-flow__wi-details'));
    if (wiDetails) wiDetails.open = true;
    setTimeout(() => {
      this.querySelector('.buybox-flow__whats-included')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 150);
  };

  // ─── Variant Sync ─────────────────────────────────────────────────────────

  /**
   * Keep the main product's Add to Cart button disabled until a filter count is
   * chosen. The product form re-enables ATC whenever we dispatch variant:update
   * (e.g. a color change made before any filter is picked), so this is
   * re-asserted after each sync and on init. Scoped to this product's section
   * so related-product quick-add buttons are left alone. No-op once a filter is
   * selected — availability then drives the button through the normal flow.
   */
  #lockAddToCartUntilFilter() {
    if (this.#filterSelected) return;
    const scope = this.closest('.shopify-section') ?? document;
    for (const el of scope.querySelectorAll('add-to-cart-component')) {
      const component = /** @type {any} */ (el);
      if (typeof component.disable === 'function') component.disable();
      const btn = /** @type {HTMLButtonElement|null} */ (el.querySelector('[ref="addToCartButton"]'));
      if (btn) btn.disabled = true;
    }
  }

  async #syncVariant() {
    const variant = this.#findVariantForSync();
    if (!variant || !this.#data) return;

    this.dispatchEvent(new VariantSelectedEvent({ id: variant.id }));

    const pageUrl = new URL(window.location.href);
    pageUrl.searchParams.set('variant', String(variant.id));
    window.history.replaceState({}, '', pageUrl.toString());

    try {
      const sectionUrl = `${this.#data.productUrl}?variant=${variant.id}&section_id=${this.#data.sectionId}`;
      const response = await fetch(sectionUrl);
      if (!response.ok) return;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Re-render Steps 2 & 3 cards so setup component variants match the new
      // color (server resolves color while keeping each system's pinned size).
      this.#refreshSetupStepsFromDoc(doc);

      this.dispatchEvent(
        new VariantUpdateEvent(
          { id: variant.id, available: variant.available, price: variant.price },
          this.dataset.sectionId || 'buybox-flow',
          { html: doc, productId: String(this.#data.productId) }
        )
      );

      // The product form just re-enabled Add to Cart off that event; keep it
      // locked while no filter count has been chosen yet.
      this.#lockAddToCartUntilFilter();
    } catch (e) {
      console.warn('BuyboxFlow: variant section fetch failed', e);
    }
  }

  /**
   * Swap the Step 2 & 3 card carousels with the freshly fetched ones so their
   * setup component variants reflect the newly selected color. The color/size
   * resolution and hide-when-unavailable logic all live server-side, so this
   * only replaces markup — it does not re-implement matching in JS. Delegated
   * click/change handlers live on the host element, so replacing card markup
   * needs no rebind. Selections reset because the variant ids change with color.
   * @param {Document} doc - Parsed section HTML for the newly selected variant.
   */
  #refreshSetupStepsFromDoc(doc) {
    const freshBuybox = doc.querySelector('buybox-flow');
    if (!freshBuybox) return;

    let changed = false;
    for (const step of [2, 3]) {
      const currentCarousel = this.querySelector(`[data-step="${step}"] .buybox-flow__carousel`);
      const freshCarousel = freshBuybox.querySelector(`[data-step="${step}"] .buybox-flow__carousel`);
      if (!currentCarousel) continue;

      // Compare the server-set card variant ids, not innerHTML — custom elements
      // (dialog-component) mutate the live markup after connecting, so an HTML
      // diff is always "changed" and would wrongly reset on a filter change.
      // Variant ids only shift when the color changes (color sync), which is
      // exactly when the cards must be re-rendered and selections reset.
      const currentIds = this.#cardVariantIds(currentCarousel);
      const freshIds = freshCarousel ? this.#cardVariantIds(freshCarousel) : [];
      if (currentIds.join(',') !== freshIds.join(',')) {
        currentCarousel.innerHTML = freshCarousel ? freshCarousel.innerHTML : '';
        changed = true;
      }
    }

    if (!changed) return;

    // Variant ids changed with color, so any prior selections are stale.
    this.#resetSetupSelections();
    // resetSetupSelections hid the overlay; on mobile re-show the Step 1 color
    // image so it doesn't vanish a moment after a color change (post-fetch).
    if (this.#mobileMq.matches) this.#refreshGalleryPreview();
    // Re-apply filter-scaled card thumbnails on the freshly rendered cards.
    this.#updateSetupCardImages();
    // Re-apply filter-scaled card prices on the freshly rendered cards.
    this.#updateSetupCardPrices();
    // Card count may have changed (a color can hide an item) — refresh dots.
    this.#rebuildAllProgress();
  }

  // ─── Carousel scroll progress ─────────────────────────────────────────────

  #initCarousels() {
    this.#carousels = [];
    for (const scroller of this.querySelectorAll('.buybox-flow__carousel-scroller')) {
      const controls = scroller.nextElementSibling;
      if (!(controls instanceof HTMLElement) || !controls.classList.contains('buybox-flow__carousel-controls')) {
        continue;
      }
      const track = /** @type {HTMLElement|null} */ (controls.querySelector('.buybox-flow__carousel-progress'));
      if (!track) continue;
      let thumb = /** @type {HTMLElement|null} */ (track.querySelector('.buybox-flow__carousel-progress-thumb'));
      if (!thumb) {
        thumb = document.createElement('div');
        thumb.className = 'buybox-flow__carousel-progress-thumb';
        track.appendChild(thumb);
      }
      const prev = /** @type {HTMLButtonElement|null} */ (controls.querySelector('[data-carousel-arrow="prev"]'));
      const next = /** @type {HTMLButtonElement|null} */ (controls.querySelector('[data-carousel-arrow="next"]'));
      const entry = { scroller: /** @type {HTMLElement} */ (scroller), controls, track, thumb, prev, next };
      this.#carousels.push(entry);
      scroller.addEventListener('scroll', () => this.#updateProgress(entry), { passive: true });
      prev?.addEventListener('click', () => this.#scrollCarousel(entry, -1));
      next?.addEventListener('click', () => this.#scrollCarousel(entry, 1));
    }
    this.#rebuildAllProgress();
  }

  /**
   * Scrolls the carousel by one card in the given direction.
   * @param {{scroller: HTMLElement}} entry
   * @param {number} dir - -1 for previous, 1 for next.
   */
  #scrollCarousel(entry, dir) {
    const { scroller } = entry;
    const list = scroller.firstElementChild;
    const item = list?.firstElementChild;
    const gap = list ? parseFloat(getComputedStyle(list).columnGap) || 0 : 0;
    const delta = item ? item.getBoundingClientRect().width + gap : scroller.clientWidth * 0.8;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    scroller.scrollBy({ left: dir * delta, behavior: reduced ? 'auto' : 'smooth' });
  }

  #rebuildAllProgress = () => {
    for (const entry of this.#carousels) this.#buildProgress(entry);
  };

  #buildProgress(entry) {
    const { scroller, controls } = entry;
    const scrollable = scroller.scrollWidth - scroller.clientWidth > 2;
    // Nothing to scroll (or the panel is collapsed → 0 width): hide the controls.
    if (!scrollable) {
      controls.hidden = true;
      return;
    }
    controls.hidden = false;
    this.#updateProgress(entry);
  }

  #updateProgress(entry) {
    const { scroller, controls, thumb, prev, next } = entry;
    if (controls.hidden) return;
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    if (maxScroll <= 0) return;
    // Thumb width = visible fraction of the track (floored so it stays visible);
    // position = how far through the scroll range we are.
    const visiblePct = Math.max((scroller.clientWidth / scroller.scrollWidth) * 100, 15);
    const progress = scroller.scrollLeft / maxScroll;
    thumb.style.width = `${visiblePct}%`;
    thumb.style.left = `${progress * (100 - visiblePct)}%`;
    if (prev) prev.disabled = scroller.scrollLeft <= 2;
    if (next) next.disabled = scroller.scrollLeft >= maxScroll - 2;
  }

  /**
   * @param {Element} carousel
   * @returns {string[]} the setup card variant ids, in order.
   */
  #cardVariantIds(carousel) {
    return [...carousel.querySelectorAll('.buybox-flow__setup-card')].map(
      el => /** @type {HTMLElement} */ (el).dataset.variantId ?? ''
    );
  }

  /**
   * Clears Step 2 & 3 selections and hides the gallery overlay so the native,
   * color-correct PDP gallery is shown again. Used when the color changes,
   * since setup selections and their variant ids no longer apply.
   */
  #resetSetupSelections() {
    for (const vid of this.#setupSelected.keys()) this.#updateCardState(vid, false);
    for (const vid of this.#accessoriesSelected.keys()) this.#updateCardState(vid, false);
    this.#setupSelected.clear();
    this.#accessoriesSelected.clear();
    this.#updateSetupWI();
    this.#updateAccessoriesWI();
    this.#hideGalleryImage();
    this.#dispatchAccessoryChange();
  }

  // ─── Step Transitions ─────────────────────────────────────────────────────

  #completeStep(stepNum) {
    const panel = /** @type {HTMLElement|null} */ (this.querySelector(`[data-step="${stepNum}"]`));
    if (!panel) return;

    this.#track('configurator_step_complete', { step: stepNum });

    let summaryText = '';
    if (stepNum === 1) {
      const parts = this.#selectedOptions.filter(Boolean);
      const variant = this.#findVariant();
      summaryText = parts.join(' · ') + (variant ? ` · ${this.#formatMoney(variant.price)}` : '');
    } else if (stepNum === 2) {
      const added = [...this.#setupSelected.values()];
      summaryText = added.length > 0 ? added.map(a => a.title).join(', ') : 'Nothing added';
    } else if (stepNum === 3) {
      const added = [...this.#accessoriesSelected.values()];
      summaryText = added.length > 0 ? added.map(a => a.title).join(', ') : 'Nothing added';
    }

    const badge = panel.querySelector('[ref^="stepBadge"]');
    if (badge) {
      badge.dataset.completed = 'true';
      badge.innerHTML = this.#checkmarkSvg(12);
    }

    const header = panel.querySelector('.buybox-flow__panel-header');

    let summaryEl = /** @type {HTMLElement|null} */ (panel.querySelector('.buybox-flow__step-summary'));
    if (!summaryEl) {
      summaryEl = document.createElement('div');
      summaryEl.className = 'buybox-flow__step-summary';
      header?.appendChild(summaryEl);
    }
    summaryEl.innerHTML = `<span class="buybox-flow__step-summary-text">${this.#escapeHtml(summaryText)}</span>`;

    if (!panel.querySelector('.buybox-flow__edit-btn')) {
      const editBtn = document.createElement('button');
      editBtn.type = 'button';
      editBtn.className = 'buybox-flow__edit-btn';
      editBtn.setAttribute('aria-label', `Edit step ${stepNum}`);
      editBtn.textContent = 'Edit';
      editBtn.addEventListener('click', () => this.#editStep(stepNum));
      header?.appendChild(editBtn);
    }

    panel.dataset.state = 'completed';

    let next = /** @type {HTMLElement|null} */ (this.querySelector(`[data-step="${stepNum + 1}"]`));
    while (next?.dataset.skip === 'true') {
      next.dataset.state = 'completed';
      next = /** @type {HTMLElement|null} */ (
        this.querySelector(`[data-step="${parseInt(next.dataset.step ?? '0') + 1}"]`)
      );
    }
    if (next) {
      next.dataset.state = 'active';
      this.#scrollPanelIntoView(next);
      // Advancing to Step 2/3 restores the composite preview (with any prior
      // selections); Step 1 shows the single Step 1 image.
      const nextStep = parseInt(next.dataset.step ?? '0');
      this.#track('configurator_step_view', { step: nextStep, trigger: 'continue' });
      this.#activePreviewStep = nextStep >= 2 ? 2 : 1;
      this.#refreshGalleryPreview();
    }
    // A newly-shown panel now has real width — dots can measure/build.
    this.#rebuildAllProgress();
  }

  #editStep(stepNum) {
    let editedPanel = null;
    this.#track('configurator_step_view', { step: stepNum, trigger: 'edit' });
    for (const panel of this.querySelectorAll('[data-step]')) {
      const el = /** @type {HTMLElement} */ (panel);
      const step = parseInt(el.dataset.step ?? '0');
      if (step === stepNum) {
        el.dataset.state = 'active';
        editedPanel = el;
      } else if (step > stepNum && el.dataset.skip !== 'true') {
        el.dataset.state = 'inactive';
      }
    }
    if (stepNum === 1 && this.#filterSelected) this.#enableContinue();
    // Editing Step 1 shows the Step 1 image; editing Step 2 the composite.
    this.#activePreviewStep = stepNum >= 2 ? 2 : 1;
    this.#refreshGalleryPreview();
    this.#rebuildAllProgress();
    if (editedPanel) this.#scrollPanelIntoView(editedPanel);
  }

  /**
   * Bring a newly-opened step into view. On mobile, align its top to the
   * viewport top (block: 'start') so the whole step is visible; on desktop just
   * nudge it into view. Deferred so the open/close height animation settles
   * first — otherwise the collapsing step above would push the target past the
   * top after the scroll.
   * @param {HTMLElement} panel
   */
  #scrollPanelIntoView(panel) {
    const isMobile = this.#mobileMq.matches;
    const block = isMobile ? 'start' : 'nearest';
    setTimeout(() => panel.scrollIntoView({ behavior: 'smooth', block }), isMobile ? 420 : 0);
  }

  // ─── UI Updates ───────────────────────────────────────────────────────────

  #enableContinue() {
    const btn = /** @type {HTMLButtonElement} */ (this.querySelector('[ref="continueCta"]'));
    const text = this.querySelector('[ref="continueText"]');
    if (!btn) return;
    // Out of stock: the resolved color + filter variant isn't purchasable, so
    // turn Continue into a "Notify me when back in stock" action that opens the
    // Klaviyo form (#handleContinue reads data-notify-mode). Pre-order variants
    // report available === true, so they stay a normal Continue.
    const variant = this.#findVariant();
    const outOfStock = Boolean(variant && variant.available === false);
    // Items In Your Order activates in step with the Add to Cart button — both
    // gate on a chosen, in-stock filter. It stays collapsed until "Review Order".
    const whatsIncluded = this.querySelector('[ref="whatsIncluded"]');
    if (whatsIncluded) whatsIncluded.setAttribute('data-state', outOfStock ? 'inactive' : 'active');
    if (outOfStock) {
      btn.disabled = false;
      btn.removeAttribute('aria-disabled');
      btn.dataset.notifyMode = 'true';
      if (text) text.textContent = this.dataset.ctaOutOfStock ?? 'Notify me when back in stock';
      return;
    }
    btn.disabled = false;
    btn.removeAttribute('aria-disabled');
    delete btn.dataset.notifyMode;
    if (text) text.textContent = this.dataset.ctaContinue ?? 'Continue';
  }

  #updateColorLabel(value) {
    const el = this.querySelector('[ref="colorLabel"]');
    if (el) el.textContent = value;
  }

  #updateFilterPrices() {
    for (const card of this.querySelectorAll('[ref="filterCard"]')) {
      const input = /** @type {HTMLInputElement} */ (card.querySelector('input[type="radio"]'));
      if (!input) continue;
      const testOptions = [...this.#selectedOptions];
      testOptions[this.#filterOptionIndex] = input.value;
      const variant = this.#variants.find(v => v.options.every((opt, i) => opt === testOptions[i]));
      if (!variant) continue;
      const priceEl = card.querySelector('.buybox-flow__filter-price');
      const compareEl = /** @type {HTMLElement|null} */ (card.querySelector('.buybox-flow__filter-compare'));
      const chipEl = /** @type {HTMLElement|null} */ (card.querySelector('.buybox-flow__filter-chip'));
      if (priceEl) priceEl.textContent = this.#formatMoney(variant.price);
      if (chipEl) chipEl.hidden = !variant.preorder;
      if (compareEl) {
        if (variant.compareAtPrice > variant.price) {
          compareEl.textContent = this.#formatMoney(variant.compareAtPrice);
          compareEl.hidden = false;
        } else {
          compareEl.textContent = '';
          compareEl.hidden = true;
        }
      }
    }
  }

  #updateWhatsIncluded() {
    const el = this.querySelector('[ref="mainItemName"]');
    if (el) el.textContent = this.#selectedOptions.filter(Boolean).join(' · ');

    // Main line price follows the resolved variant.
    const variant = this.#findVariant();
    const priceEl = this.querySelector('[ref="wiMainPrice"]');
    if (priceEl && variant) priceEl.textContent = this.#formatMoney(variant.price);

    // Box contents: "(set of {count})" tracks the chosen filter count.
    const count = String(this.#selectedOptions[this.#filterOptionIndex] ?? '').match(/\d+/)?.[0];
    if (count) {
      for (const span of this.querySelectorAll('[data-wi-filter-count]')) span.textContent = count;
    }
  }

  #updateCardState(variantId, added) {
    const btn = /** @type {HTMLElement|null} */ (
      this.querySelector(`.buybox-flow__setup-add[data-variant-id="${variantId}"]`)
    );
    if (!btn) return;
    btn.setAttribute('aria-pressed', String(added));
    const icon = btn.querySelector('.buybox-flow__setup-add-icon');
    if (icon) icon.innerHTML = added ? this.#checkmarkSvg(14) : '+';
    const label = btn.querySelector('.buybox-flow__setup-add-label');
    if (label) label.textContent = added ? 'Added' : 'Add';
    const card = /** @type {HTMLElement|null} */ (btn.closest('.buybox-flow__setup-card'));
    if (card) card.setAttribute('data-added', String(added));
  }

  #updateSetupWI() {
    const list = this.querySelector('.buybox-flow__wi-list');
    if (!list) return;
    for (const item of list.querySelectorAll('[data-setup-item]')) item.remove();
    for (const item of this.#setupSelected.values()) {
      const title = this.#stripSystemName(item.title);
      let label = item.color ? `${title} - ${item.color}` : title;
      // Filter-scaled items (e.g. Fluoride Filters) show the chosen filter count
      // alongside the name, matching the main line ("... · 2 Filters").
      if (item.qtyMatchesFilters) {
        const filterValue = this.#selectedOptions[this.#filterOptionIndex];
        if (filterValue) label += ` · ${filterValue}`;
      }
      const li = document.createElement('li');
      li.className = 'buybox-flow__wi-item';
      li.dataset.setupItem = '1';
      li.innerHTML = `<span class="buybox-flow__wi-icon" aria-hidden="true">${this.#checkmarkSvg(10)}</span><span>${this.#escapeHtml(label)}</span><span class="buybox-flow__wi-item-price">${this.#formatMoney(item.price * this.#itemQuantity(item))}</span>`;
      list.appendChild(li);
    }
  }

  #updateAccessoriesWI() {
    const list = this.querySelector('.buybox-flow__wi-list');
    if (!list) return;
    for (const item of list.querySelectorAll('[data-accessory-item]')) item.remove();
    for (const item of this.#accessoriesSelected.values()) {
      const li = document.createElement('li');
      li.className = 'buybox-flow__wi-item';
      li.dataset.accessoryItem = '1';
      li.innerHTML = `<span class="buybox-flow__wi-icon" aria-hidden="true">${this.#checkmarkSvg(10)}</span><span>${this.#escapeHtml(this.#stripSystemName(item.title))}</span><span class="buybox-flow__wi-item-price">${this.#formatMoney(item.price * this.#itemQuantity(item))}</span>`;
      list.appendChild(li);
    }
  }

  /**
   * Drop the "Legacy"/"Compact" system words from an order-summary line label,
   * mirroring the Step 2/3 card display.
   * @param {string} title
   * @returns {string}
   */
  #stripSystemName(title) {
    return String(title ?? '')
      .replace(/\bLegacy\b/g, '')
      .replace(/\bCompact\b/g, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  /**
   * Quantity multiplier from the current filter count for items flagged
   * quantity_matches_filters (e.g. Fluoride: 2 filters -> 1, 4 filters -> 2).
   * Computed live so editing the filter after selecting updates the quantity.
   * @returns {number}
   */
  #filterQuantityMultiplier() {
    const filterValue = this.#selectedOptions[this.#filterOptionIndex] ?? '';
    const count = parseInt(String(filterValue).match(/\d+/)?.[0] ?? '0', 10);
    if (!count) return 1;
    return Math.max(1, Math.round(count / 2));
  }

  /** Effective quantity for a stored setup/accessory item. */
  #itemQuantity(item) {
    return item?.qtyMatchesFilters ? this.#filterQuantityMultiplier() : 1;
  }

  /**
   * Swap each filter-scaled setup card's thumbnail to its alternate image when
   * the quantity is doubled (e.g. Fluoride shows its 4-filter image at 4
   * filters), and back to the default otherwise. Runs for all matching cards
   * regardless of selection, so the card reflects the current filter count.
   */
  #updateSetupCardImages() {
    const doubled = this.#filterQuantityMultiplier() >= 2;
    for (const img of this.querySelectorAll('.buybox-flow__setup-card-img[data-card-image-alt]')) {
      const el = /** @type {HTMLImageElement} */ (img);
      const target = doubled ? el.dataset.cardImageAlt : el.dataset.cardImageDefault;
      if (target && el.getAttribute('src') !== target) el.src = target;
    }
  }

  /**
   * Scale each filter-matched setup card's visible price (and its dialog confirm
   * label) to the current filter count — e.g. Fluoride shows 2× at 4 filters —
   * so the card matches the quantity actually added to cart. Mirrors
   * #updateSetupCardImages, which swaps the thumbnail for the same cards.
   */
  #updateSetupCardPrices() {
    const multiplier = this.#filterQuantityMultiplier();
    const confirmBtns = this.querySelectorAll('.buybox-flow__setup-confirm[data-qty-matches-filters="true"]');
    for (const btn of confirmBtns) {
      const basePrice = parseInt(/** @type {HTMLElement} */ (btn).dataset.basePrice ?? '0', 10);
      if (!basePrice) continue;
      const item = btn.closest('.buybox-flow__carousel-item');
      const priceEl = /** @type {HTMLElement|null} */ (item?.querySelector('.buybox-flow__setup-card-price'));
      if (!priceEl) continue;
      const currentEl = /** @type {HTMLElement} */ (
        priceEl.querySelector('.buybox-flow__setup-card-current') ?? priceEl
      );
      currentEl.textContent = this.#formatMoney(basePrice * multiplier);
      // The compare-at doubles with the quantity too (#551): there is no stored
      // set-of-4 figure, it is calculated (base compare × multiplier).
      const compareEl = /** @type {HTMLElement|null} */ (priceEl.querySelector('.buybox-flow__setup-card-compare'));
      const baseCompare = parseInt(/** @type {HTMLElement} */ (btn).dataset.baseCompare ?? '0', 10);
      if (compareEl && baseCompare > 0) compareEl.textContent = this.#formatMoney(baseCompare * multiplier);
    }
  }

  #dispatchAccessoryChange() {
    if (!this.#data) return;
    // Step 2 (setup) items are flagged `nested` so the product form adds them
    // as nested cart lines under the main system (Shopify parent_id). Step 3
    // accessories stay as regular flat lines.
    const allSelected = [
      ...[...this.#setupSelected.values()].map(acc => ({ ...acc, nested: true })),
      ...this.#accessoriesSelected.values(),
    ];
    const variant = this.#findVariant();
    this.dispatchEvent(
      new AccessoryChangeEvent({
        accessoryVariantIds: allSelected.map(a => a.variantId),
        accessories: allSelected.map(acc => ({
          variantId: parseInt(acc.variantId),
          title: acc.title,
          price: acc.price,
          quantity: this.#itemQuantity(acc),
          ...(acc.sellingPlanId ? { sellingPlanId: acc.sellingPlanId } : {}),
          ...(acc.nested ? { nested: true } : {}),
        })),
        productId: String(this.#data.productId),
        variantId: String(variant?.id ?? this.#data.selectedVariantId),
      })
    );
  }

  // ─── Book a Call ────────────────────────────────────────────────────────────

  /**
   * Consultation availability is the BOROUX team's hours, defined in their
   * business timezone. Slots are generated at these wall-clock windows in that
   * zone, then displayed converted to the shopper's local timezone. To change
   * the team's zone, edit #bookBusinessTz only.
   */
  #bookBusinessTz = 'America/Denver';
  #bookSlotMinutes = 30;
  // Windows in minutes-from-midnight (business time): 8:30–11:30 and 1:30–4:00.
  #bookWindows = [
    { start: 8 * 60 + 30, end: 11 * 60 + 30 },
    { start: 13 * 60 + 30, end: 16 * 60 },
  ];

  #initBookCall() {
    const dialog = /** @type {HTMLDialogElement|null} */ (this.querySelector('[ref="bookCallDialog"]'));
    if (!dialog) return;

    this.querySelector('[ref="bookCallTrigger"]')?.addEventListener('click', async event => {
      // The trigger now lives inside the filter-help popup. Close that dialog
      // first (awaiting its close animation + scroll restore) so we don't stack
      // two modals, then open the book-a-call dialog.
      const helpDialog = /** @type {any} */ (event.currentTarget).closest('dialog-component');
      if (helpDialog?.closeDialog) await helpDialog.closeDialog();
      this.#openBookCall();
    });
    this.querySelector('[ref="bookCallClose"]')?.addEventListener('click', () => this.#closeBookCall());
    this.querySelector('[ref="quickChatTrigger"]')?.addEventListener('click', () => this.#openQuickChat());
    this.querySelector('[ref="bookCallForm"]')?.addEventListener('submit', this.#handleBookSubmit);

    // Backdrop click + Esc close, and restore body scroll on any close.
    dialog.addEventListener('click', event => {
      if (event.target === dialog) this.#closeBookCall();
    });
    dialog.addEventListener('cancel', event => {
      event.preventDefault();
      this.#closeBookCall();
    });
    dialog.addEventListener('close', () => {
      document.body.style.overflow = '';
    });

    this.querySelector('[ref="bookCallSlots"]')?.addEventListener('click', event => {
      const btn = /** @type {HTMLElement} */ (event.target).closest('.buybox-flow__book-slot');
      if (btn) this.#selectSlot(/** @type {HTMLElement} */ (btn));
    });
  }

  /**
   * Closes the Book a Call dialog, playing the sheet slide-out first when one
   * applies (mobile). Desktop has no closing animation, so it closes at once.
   */
  #closeBookCall() {
    const dialog = /** @type {HTMLDialogElement|null} */ (this.querySelector('[ref="bookCallDialog"]'));
    if (!dialog?.open || dialog.classList.contains('dialog-closing')) return;

    dialog.classList.add('dialog-closing');
    const finish = () => {
      dialog.classList.remove('dialog-closing');
      dialog.close();
    };

    if (getComputedStyle(dialog).animationName === 'none') {
      finish();
      return;
    }

    dialog.addEventListener('animationend', function handler(event) {
      if (event.target !== dialog) return;
      dialog.removeEventListener('animationend', handler);
      finish();
    });
  }

  #openBookCall() {
    const dialog = /** @type {HTMLDialogElement|null} */ (this.querySelector('[ref="bookCallDialog"]'));
    if (!dialog) return;

    // Reset to the initial (form) state each open.
    const form = /** @type {HTMLFormElement|null} */ (this.querySelector('[ref="bookCallForm"]'));
    const success = this.querySelector('[ref="bookCallSuccess"]');
    const chat = this.querySelector('.buybox-flow__book-chat');
    const hours = this.querySelector('.buybox-flow__book-hours');
    const error = this.querySelector('[ref="bookCallError"]');

    form?.reset();
    for (const ref of ['bookCallName', 'bookCallPhone']) {
      this.querySelector(`[ref="${ref}"]`)?.removeAttribute('aria-invalid');
    }
    this.#bookSlot = null;
    if (form) form.hidden = false;
    if (success) success.hidden = true;
    if (chat) chat.hidden = false;
    if (hours) hours.hidden = false;
    if (error) error.hidden = true;

    this.#buildTimeSlots();

    this.#track('configurator_modal_open', { modal_type: 'book_call' });

    document.body.style.overflow = 'hidden';
    dialog.showModal();
  }

  /**
   * Builds upcoming 30-minute slots from the team's business-hours windows,
   * displayed in the shopper's LOCAL timezone. The stored value is the slot's
   * absolute start time (ISO UTC) so the team knows exactly when to call.
   */
  #buildTimeSlots() {
    const container = this.querySelector('[ref="bookCallSlots"]');
    if (!container) return;

    const tz = this.#bookBusinessTz;
    const now = new Date();
    const leadMs = 60 * 60 * 1000; // require at least 1 hour lead time

    // Show the shopper which timezone the slots are in (their local zone).
    const note = this.querySelector('[ref="bookSlotsTzNote"]');
    if (note) note.textContent = '(your local time)';

    // Today's calendar date in the business timezone.
    const bp = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now);
    const bv = t => parseInt(bp.find(p => p.type === t)?.value ?? '0', 10);
    const anchor = new Date(Date.UTC(bv('year'), bv('month') - 1, bv('day')));

    const MAX = 8;
    const slots = [];
    for (let offset = 0; offset < 10 && slots.length < MAX; offset++) {
      const date = new Date(anchor);
      date.setUTCDate(anchor.getUTCDate() + offset);
      if (date.getUTCDay() === 0 || date.getUTCDay() === 6) continue; // Mon–Fri only (business days)

      for (const w of this.#bookWindows) {
        for (let min = w.start; min + this.#bookSlotMinutes <= w.end; min += this.#bookSlotMinutes) {
          const start = this.#wallTimeToUtc(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), min, tz);
          if (start.getTime() < now.getTime() + leadMs) continue; // skip past / too-soon slots
          const end = new Date(start.getTime() + this.#bookSlotMinutes * 60000);
          slots.push({ start, end });
          if (slots.length >= MAX) break;
        }
        if (slots.length >= MAX) break;
      }
    }

    container.innerHTML = slots
      .map((slot, i) => {
        const { day, time, full } = this.#formatSlotLabel(slot.start, slot.end);
        return (
          `<button type="button" class="buybox-flow__book-slot" role="radio" aria-checked="false" aria-pressed="false"` +
          ` data-value="${this.#escapeHtml(slot.start.toISOString())}" data-label="${this.#escapeHtml(full)}">` +
          `<span class="buybox-flow__book-slot-top">` +
          (i === 0 ? '<span class="buybox-flow__book-slot-badge">Soonest</span>' : '') +
          `<span class="buybox-flow__book-slot-day">${this.#escapeHtml(day)}</span></span>` +
          `<span class="buybox-flow__book-slot-time">${this.#escapeHtml(time)}</span></button>`
        );
      })
      .join('');

    const first = /** @type {HTMLElement|null} */ (container.querySelector('.buybox-flow__book-slot'));
    if (first) this.#selectSlot(first);
  }

  /**
   * Converts a wall-clock time in a given IANA timezone to an absolute UTC Date,
   * correctly accounting for that zone's offset/DST at that instant.
   */
  #wallTimeToUtc(year, month0, day, minutes, timeZone) {
    const hh = Math.floor(minutes / 60);
    const mm = minutes % 60;
    const guess = Date.UTC(year, month0, day, hh, mm);
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(new Date(guess));
    const gv = t => parseInt(parts.find(p => p.type === t)?.value ?? '0', 10);
    let shownHour = gv('hour');
    if (shownHour === 24) shownHour = 0;
    const shownAsUtc = Date.UTC(gv('year'), gv('month') - 1, gv('day'), shownHour, gv('minute'), gv('second'));
    return new Date(guess - (shownAsUtc - guess));
  }

  /** Human label in the shopper's local timezone, e.g. "Today 10:30-11:00 AM". */
  #formatSlotLabel(start, end) {
    const now = new Date();
    const tmr = new Date(now);
    tmr.setDate(now.getDate() + 1);
    const key = dt => new Intl.DateTimeFormat('en-CA').format(dt); // YYYY-MM-DD (local)
    const startKey = key(start);
    let day;
    if (startKey === key(now)) day = 'Today';
    else if (startKey === key(tmr)) day = 'Tomorrow';
    else day = new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(start);

    const fmtTime = dt =>
      new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).format(dt);
    const startStr = fmtTime(start);
    const endStr = fmtTime(end);
    const mer = s => s.slice(-2);
    // Drop the start meridiem when both ends share it: "10:30-11:00 AM".
    const startTrim = mer(startStr) === mer(endStr) ? startStr.replace(/\s?[AP]M$/, '') : startStr;
    const time = `${startTrim}-${endStr}`;
    return { day, time, full: `${day} ${time}` };
  }

  #selectSlot(btn) {
    const container = this.querySelector('[ref="bookCallSlots"]');
    if (!container) return;
    for (const b of container.querySelectorAll('.buybox-flow__book-slot')) {
      const on = b === btn;
      b.setAttribute('aria-pressed', String(on));
      b.setAttribute('aria-checked', String(on));
    }
    this.#bookSlot = { value: btn.dataset.value ?? '', label: btn.dataset.label ?? btn.textContent?.trim() ?? '' };
  }

  #openQuickChat() {
    this.#closeBookCall();
    const chat = /** @type {any} */ (window).GorgiasChat;
    if (chat && typeof chat.open === 'function') {
      chat.open();
      return;
    }
    // Fallback: click the Gorgias chat launcher if the SDK global isn't ready.
    const launcher = /** @type {HTMLElement|null} */ (
      document.querySelector('#gorgias-chat-container button, [id^="gorgias-chat"] button, #chat-button')
    );
    if (launcher) launcher.click();
    else console.warn('[buybox] Gorgias chat widget not found');
  }

  #handleBookSubmit = async event => {
    event.preventDefault();
    const name = /** @type {HTMLInputElement|null} */ (this.querySelector('[ref="bookCallName"]'));
    const phone = /** @type {HTMLInputElement|null} */ (this.querySelector('[ref="bookCallPhone"]'));
    const error = this.querySelector('[ref="bookCallError"]');
    const submit = this.querySelector('[ref="bookCallSubmit"]');
    if (!name || !phone) return;

    const nameOk = name.value.trim().length > 0;
    const phoneOk = (phone.value.match(/\d/g) || []).length >= 10;
    name.setAttribute('aria-invalid', String(!nameOk));
    phone.setAttribute('aria-invalid', String(!phoneOk));

    if (!nameOk || !phoneOk || !this.#bookSlot) {
      this.#showBookError(
        !this.#bookSlot && nameOk && phoneOk
          ? 'Please pick a time.'
          : 'Please enter your name and a valid phone number.'
      );
      return;
    }
    if (error) error.hidden = true;

    const cfg = this.#data?.bookCall ?? {};
    const payload = {
      name: name.value.trim(),
      phone: phone.value.trim(),
      timeSlot: this.#bookSlot.value,
      timeSlotLabel: this.#bookSlot.label,
      productTitle: cfg.productTitle,
      productHandle: cfg.productHandle,
      variantId: String(this.#findVariant()?.id ?? this.#data?.selectedVariantId ?? ''),
      pageUrl: window.location.href,
    };

    if (!cfg.endpoint) {
      // Endpoint not configured yet — surface the payload for wiring/testing.
      console.warn('[buybox] book-a-call endpoint not set; payload:', payload);
      this.#showBookSuccess();
      return;
    }

    submit?.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(cfg.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      this.#showBookSuccess();
    } catch (err) {
      console.warn('[buybox] book-a-call submit failed', err);
      this.#showBookError('Something went wrong. Please try again or use Quick chat.');
    } finally {
      submit?.removeAttribute('aria-busy');
    }
  };

  #showBookError(message) {
    const error = this.querySelector('[ref="bookCallError"]');
    if (!error) return;
    error.textContent = message;
    error.hidden = false;
  }

  #showBookSuccess() {
    this.#track('configurator_book_call', { action: 'submit' });
    const form = this.querySelector('[ref="bookCallForm"]');
    const success = this.querySelector('[ref="bookCallSuccess"]');
    const chat = this.querySelector('.buybox-flow__book-chat');
    const hours = this.querySelector('.buybox-flow__book-hours');
    if (form) form.hidden = true;
    if (chat) chat.hidden = true;
    if (hours) hours.hidden = true;
    if (success) success.hidden = false;
  }

  // ─── Gallery Preview ──────────────────────────────────────────────────────

  /**
   * Decides which single preview image (if any) should cover the PDP gallery,
   * then shows or hides the overlay accordingly.
   *
   * Priority: most recently added Step 2 setup item with a mapped image →
   * current system variant's image → filter-count fallback → nothing.
   */
  #refreshGalleryPreview() {
    // Split Step 2 selections: transparent PNGs stack as overlay layers, while
    // any "full image" item (an opaque composite that can't be layered, e.g.
    // Fluoride) is held aside — it only renders when nothing else is layered.
    /** @type {{url: string, alt: string}[]} */
    const componentLayers = [];
    /** @type {{url: string, alt: string}|null} */
    let fullImageLayer = null;
    for (const [variantId, item] of this.#setupSelected) {
      // When the item's quantity is doubled (e.g. Fluoride at 4 filters), use
      // its alternate mapped overlay if one exists; otherwise the normal one.
      const doubled = this.#itemQuantity(item) >= 2;
      const url = (doubled && this.#componentImagesAlt[variantId]) || this.#componentImages[variantId];
      if (!url) continue;
      if (this.#isFullImageItem(item?.title)) {
        fullImageLayer = { url, alt: item?.title ?? '' };
      } else {
        componentLayers.push({ url, alt: item?.title ?? '' });
      }
    }

    // When the shopper is interacting with Step 1 (even after adding Step 2
    // items and coming back to edit), show the Step 1 image only — not the Step
    // 2 composite. The composite returns as soon as they act on Step 2 again.
    const onStep1 = this.#activePreviewStep === 1;

    // Base layer.
    /** @type {{url: string, alt: string}|null} */
    let baseLayer = null;
    const hasStep2Selection = componentLayers.length > 0 || fullImageLayer !== null;
    const colorValue = this.#selectedOptions[this.#colorOptionIndex];
    const colorImageUrl = colorValue ? this.#colorImages[colorValue] : null;
    if (!onStep1) {
      // Step 2/3 base (mobile + desktop). With nothing added yet, show the
      // dedicated color image (custom.buybox_color_image). Once a component is
      // selected, revert to the Step 2 base composite the transparent overlays
      // are aligned to (previous behavior). Fall back to the variant's featured
      // image if neither image is set.
      const variant = this.#findVariant() ?? this.#findVariantByColor();
      const base = hasStep2Selection
        ? this.#step2BaseImage || colorImageUrl
        : colorImageUrl || this.#step2BaseImage;
      const url = base || variant?.featuredImage;
      if (url) baseLayer = { url, alt: variant?.title ?? '' };
    } else if (this.#mobileMq.matches && (onStep1 || !hasStep2Selection)) {
      // Mobile Step 1 inline mount, so the shopper doesn't scroll up to the PDP
      // gallery. Color change -> the color's featured image; filter change -> that
      // filter size's own layered/mapped image.
      const variant = this.#findVariant();
      if (this.#step1PreviewMode === 'filter' && this.#filterSelected) {
        const filterValue = this.#selectedOptions[this.#filterOptionIndex] ?? '';
        const variantUrl = variant && this.#componentImages[String(variant.id)];
        const filterUrl = filterValue && this.#componentImagesByFilter[filterValue];
        const baseUrl = variantUrl || filterUrl;
        if (baseUrl) baseLayer = { url: baseUrl, alt: variant?.title ?? '' };
      } else {
        // Color mode or initial load (no interaction yet): show the dedicated
        // buybox color image (custom.buybox_color_image) so Step 1 can differ
        // from the PDP gallery; fall back to the selected variant's featured
        // image. Before a filter count is chosen the filter option is empty, so
        // #findVariant() can't resolve a full variant — fall back to any variant
        // matching just the chosen color.
        const colorVariant = variant ?? this.#findVariantByColor();
        const url = colorImageUrl || colorVariant?.featuredImage;
        if (url) {
          baseLayer = { url, alt: colorVariant?.title ?? '' };
        }
      }
    } else if (this.#filterSelected) {
      const variant = this.#findVariant();
      const filterValue = this.#selectedOptions[this.#filterOptionIndex] ?? '';
      const variantUrl = variant && this.#componentImages[String(variant.id)];
      const filterUrl = filterValue && this.#componentImagesByFilter[filterValue];
      const baseUrl = variantUrl || filterUrl;
      if (baseUrl) baseLayer = { url: baseUrl, alt: variant?.title ?? '' };
    }

    // Fluoride is a full composite that stands alone — it swaps with the
    // layered component composite based on the most recently selected Step 2
    // item. If Fluoride was picked last, show base + Fluoride only. If a
    // component was picked last, show base + component layers (no Fluoride).
    const setupKeys = [...this.#setupSelected.keys()];
    const lastSetupId = setupKeys[setupKeys.length - 1];
    const lastIsFullImage = lastSetupId ? this.#isFullImageItem(this.#setupSelected.get(lastSetupId)?.title) : false;
    const showFullImage = fullImageLayer !== null && lastIsFullImage;

    const layers = [];
    if (baseLayer) layers.push(baseLayer);
    // On Step 1 the base image stands alone — no Step 2 component overlays.
    if (!onStep1) {
      if (showFullImage) {
        layers.push(/** @type {{url: string, alt: string}} */ (fullImageLayer));
      } else {
        layers.push(...componentLayers);
      }
    }

    console.log('[buybox] refreshGalleryPreview layers', {
      filterSelected: this.#filterSelected,
      setupSelected: [...this.#setupSelected.keys()],
      fullImageKeywords: this.#fullImageTitleKeywords,
      fullImageLayer,
      lastIsFullImage,
      showFullImage,
      step2BaseImage: this.#step2BaseImage,
      colorImageUrl,
      componentImagesKeys: Object.keys(this.#componentImages),
      componentImagesByFilterKeys: Object.keys(this.#componentImagesByFilter),
      layerCount: layers.length,
      layers,
    });

    if (layers.length === 0) {
      this.#hideGalleryImage();
      return;
    }
    this.#renderGalleryLayers(layers);
  }

  /**
   * @param {string|undefined} title
   * @returns {boolean} true if the item's title marks it as a full-image (opaque base) item
   */
  #isFullImageItem(title) {
    if (!title) return false;
    const lower = title.toLowerCase();
    return this.#fullImageTitleKeywords.some(kw => lower.includes(kw));
  }

  /**
   * Chooses where the preview overlay mounts:
   * - Mobile, on Step 1: inline after Step 1 (system/base preview).
   * - Mobile, on Step 2+ (or with Step 2 items selected): inline in the Step 2
   *   block, so the base/composite shows there by default.
   * - Desktop: over the PDP product media.
   * @returns {{ wrap: HTMLElement|null, inline: boolean }}
   */
  #galleryMount() {
    if (this.#mobileMq.matches) {
      const onStep2 = this.#activePreviewStep >= 2 || this.#setupSelected.size > 0;
      const step2Mount = onStep2
        ? /** @type {HTMLElement|null} */ (this.querySelector('[ref="galleryMobileMountStep2"]'))
        : null;
      const mount =
        step2Mount ?? /** @type {HTMLElement|null} */ (this.querySelector('[ref="galleryMobileMount"]'));
      if (mount) return { wrap: mount, inline: true };
    }
    const media = /** @type {HTMLElement|null} */ (
      this.closest('.product-information')?.querySelector('.product-information__media') ??
        document.querySelector('.product-information__media')
    );
    return { wrap: media, inline: false };
  }

  #ensureOverlay() {
    const { wrap, inline } = this.#galleryMount();
    if (!wrap) return null;

    if (!this.#overlayEl) {
      const overlay = document.createElement('div');
      overlay.className = 'buybox-flow__gallery-overlay';
      overlay.hidden = true;
      const stage = document.createElement('div');
      stage.className = 'buybox-flow__gallery-stage';
      overlay.appendChild(stage);
      this.#overlayEl = overlay;
    }

    // Move the overlay to the correct mount when the viewport crosses the breakpoint.
    if (this.#overlayEl.parentElement !== wrap) wrap.appendChild(this.#overlayEl);
    this.#overlayEl.classList.toggle('buybox-flow__gallery-overlay--inline', inline);
    // The absolute (desktop) overlay needs a positioned ancestor; inline doesn't.
    if (!inline && getComputedStyle(wrap).position === 'static') wrap.style.position = 'relative';

    return this.#overlayEl;
  }

  /**
   * Renders an ordered stack of PNG layers into the sticky stage. Layers whose
   * URL is unchanged are left untouched (so re-selecting a filter doesn't flash
   * the stack). When a layer's image changes, the new image is faded in on top
   * of the old one and the old is removed once the fade completes — a true
   * cross-dissolve. Layers no longer present fade out and are removed.
   *
   * @param {{url: string, alt: string}[]} layers - Bottom-to-top layer list.
   */
  #renderGalleryLayers(layers) {
    const overlay = this.#ensureOverlay();
    if (!overlay) {
      console.warn('[buybox] no gallery wrapper found (.product-information__media) — cannot show overlay');
      return;
    }
    const stage = /** @type {HTMLElement|null} */ (overlay.querySelector('.buybox-flow__gallery-stage'));
    if (!stage) return;

    const animate = !this.#reduceMotionMq.matches;

    layers.forEach((layer, i) => {
      // The live (not-fading-out) image currently occupying this layer slot.
      const current = /** @type {HTMLImageElement|null} */ (
        stage.querySelector(`.buybox-flow__gallery-layer[data-layer-index="${i}"]:not([data-fading-out])`)
      );
      if (current && current.getAttribute('src') === layer.url) {
        // Unchanged — keep it, just sync decorative attributes.
        current.alt = i === 0 ? layer.alt : '';
        current.style.zIndex = String(i);
        return;
      }

      const img = document.createElement('img');
      img.className = 'buybox-flow__gallery-layer';
      img.dataset.layerIndex = String(i);
      // Only the base layer carries alt text; upper layers are decorative.
      img.alt = i === 0 ? layer.alt : '';
      img.style.zIndex = String(i);
      img.src = layer.url;

      if (animate) {
        // Start transparent, then fade in on top of the outgoing image.
        img.style.opacity = '0';
        stage.appendChild(img);
        const reveal = () =>
          requestAnimationFrame(() => {
            img.style.opacity = '1';
          });
        if (typeof img.decode === 'function') img.decode().then(reveal).catch(reveal);
        else reveal();
        if (current) this.#fadeOutGalleryLayer(current);
      } else {
        if (current) current.remove();
        stage.appendChild(img);
      }
    });

    // Fade out any layers beyond the current stack depth.
    for (const el of stage.querySelectorAll('.buybox-flow__gallery-layer:not([data-fading-out])')) {
      const layerEl = /** @type {HTMLImageElement} */ (el);
      if (parseInt(layerEl.dataset.layerIndex ?? '0') >= layers.length) {
        if (animate) this.#fadeOutGalleryLayer(layerEl);
        else layerEl.remove();
      }
    }

    overlay.hidden = false;
    // Illustration-preview impressions, once per step per page load. The #457
    // back-to-gallery control will report here as {action: 'back_to_gallery'}.
    this.#trackOnce(
      'configurator_gallery',
      { action: 'preview_shown', step: this.#activePreviewStep },
      `preview${this.#activePreviewStep}`
    );
  }

  /**
   * Fades a gallery layer out and removes it once the transition ends (with a
   * timeout fallback in case transitionend never fires).
   * @param {HTMLImageElement} img
   */
  #fadeOutGalleryLayer(img) {
    img.dataset.fadingOut = 'true';
    const remove = () => img.remove();
    img.addEventListener('transitionend', remove, { once: true });
    setTimeout(remove, 500);
    requestAnimationFrame(() => {
      img.style.opacity = '0';
    });
  }

  #hideGalleryImage() {
    if (this.#overlayEl) this.#overlayEl.hidden = true;
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  #findVariant() {
    return this.#variants.find(v => v.options.every((opt, i) => opt === this.#selectedOptions[i]));
  }

  /**
   * Finds any variant matching the currently selected color, ignoring the
   * filter count. Used for the mobile Step 1 color preview when no filter has
   * been chosen yet and a full variant can't be resolved.
   * @returns {object|undefined}
   */
  #findVariantByColor() {
    const color = this.#selectedOptions[this.#colorOptionIndex];
    if (!color) return undefined;
    return this.#variants.find(v => v.options[this.#colorOptionIndex] === color);
  }

  #findVariantForSync() {
    if (this.#filterSelected) return this.#findVariant();
    const opts = [...this.#selectedOptions];
    opts[this.#filterOptionIndex] = this.#data?.initialOptions[this.#filterOptionIndex] ?? '';
    return this.#variants.find(v => v.options.every((opt, i) => opt === opts[i]));
  }

  #formatMoney(cents) {
    if (cents == null || !this.#data) return '';
    const format = this.#data.useCurrency ? this.#data.moneyWithCurrencyFormat : this.#data.moneyFormat;
    const amount = (cents / 100).toFixed(2);
    const amountNoDecimals = String(Math.floor(cents / 100));
    return format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
      .replace(
        /\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g,
        amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      )
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  }

  #checkmarkSvg(size = 12) {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" width="${size}" height="${size}"><polyline points="14 4 6 12 2 8"></polyline></svg>`;
  }

  #escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

if (!customElements.get('buybox-flow')) {
  customElements.define('buybox-flow', BuyboxFlow);
}
